import { API_CONFIG, QUICK_SIGNUP_STORAGE_KEY } from '../config/envConfig';

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';
const RETRY_DELAY_MS = 2000;
const MAX_RETRIES = 20;

let scriptLoadPromise = null;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const loadRazorpayScript = () => {
  if (window.Razorpay) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error('Unable to load payment gateway. Please try again.'));
    };
    document.body.appendChild(script);
  });

  return scriptLoadPromise;
};

const verifyPayment = async (paymentResponse) => {
  const response = await fetch(API_CONFIG.buyNowVerifyPaymentUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      razorpay_order_id: paymentResponse.razorpay_order_id,
      razorpay_payment_id: paymentResponse.razorpay_payment_id,
      razorpay_signature: paymentResponse.razorpay_signature,
    }),
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok || data.success === false) {
    throw new Error(data.error || data.message || 'Invalid payment signature');
  }

  return data;
};

// Polls for the post-payment OTP — 402 means the payment webhook hasn't landed yet.
const fetchOtp = async (orderId) => {
  for (let attempt = 0; attempt < MAX_RETRIES; attempt += 1) {
    const response = await fetch(API_CONFIG.buyNowPaymentSuccessTokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ razorpay_order_id: orderId }),
    });
    const data = await response.json().catch(() => ({}));

    if (response.status === 402) {
      await sleep(RETRY_DELAY_MS);
      continue;
    }

    if (!response.ok || data.success === false) {
      throw new Error(data.error || data.message || 'Unable to confirm payment. Please try again.');
    }

    if (data.email && data.otp) {
      return data;
    }

    await sleep(RETRY_DELAY_MS);
  }

  throw new Error('Payment confirmation timed out. Please try again in a moment.');
};

/**
 * Verifies a Checkout.js payment and waits for the post-payment OTP.
 * Called from /payment/success so the user sees that page during verify.
 */
export const confirmCheckoutPayment = async (orderId, paymentResponse) => {
  await verifyPayment(paymentResponse);
  return fetchOtp(orderId);
};

/**
 * Opens the Razorpay Checkout.js modal for a buy-now order.
 * On success, go to /payment/success immediately — verify-payment and the
 * next redirect (onboarding or the app) happen there.
 */
export const openRazorpayCheckout = async ({
  orderId,
  amount,
  currency,
  key,
  email,
  companyWebsite,
  navigate,
  onError,
  onCancel,
}) => {
  try {
    await loadRazorpayScript();

    const rzp = new window.Razorpay({
      key,
      amount,
      currency: currency || 'INR',
      order_id: orderId,
      name: 'WhiteOwl',
      prefill: email ? { email } : undefined,
      handler: (paymentResponse) => {
        const payload = {
          flow: 'payment',
          confirming: true,
          orderId,
          paymentResponse,
          suggested_website: companyWebsite || '',
          email: email || '',
        };
        sessionStorage.setItem(QUICK_SIGNUP_STORAGE_KEY, JSON.stringify(payload));
        navigate('/payment/success', { replace: true, state: payload });
      },
      modal: {
        ondismiss: () => onCancel?.(),
      },
    });

    rzp.open();
  } catch (err) {
    onError?.(err.message || 'Unable to start checkout. Please try again.');
  }
};
